"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var SortedMap_1 = require("./SortedMap");
var Path_1 = require("./Path");
var util_1 = require("./util");
var util_2 = require("@firebase/util");
var emptyChildrenSingleton;
/**
 * Singleton empty children collection.
 *
 * @const
 * @type {!SortedMap.>}
 */
var EmptyChildren = function () {
 if (!emptyChildrenSingleton) {
 emptyChildrenSingleton = new SortedMap_1.SortedMap(util_1.stringCompare);
 }
 return emptyChildrenSingleton;
};
/**
 * A tree with immutable elements.
 */
var ImmutableTree = /** @class */ (function () {
 /**
 * @template T
 * @param {?T} value
 * @param {SortedMap.>=} children
 */
 function ImmutableTree(value, children) {
 if (children === void 0) { children = EmptyChildren(); }
 this.value = value;
 this.children = children;
 }
 /**
 * @template T
 * @param {!Object.} obj
 * @return {!ImmutableTree.}
 */
 ImmutableTree.fromObject = function (obj) {
 var tree = ImmutableTree.Empty;
 util_2.forEach(obj, function (childPath, childSnap) {
 tree = tree.set(new Path_1.Path(childPath), childSnap);
 });
 return tree;
 };
 /**
 * True if the value is empty and there are no children
 * @return {boolean}
 */
 ImmutableTree.prototype.isEmpty = function () {
 return this.value === null && this.children.isEmpty();
 };
 /**
 * Given a path and predicate, return the first node and the path to that node
 * where the predicate returns true.
 *
 * TODO Do a perf test -- If we're creating a bunch of {path: value:} objects
 * on the way back out, it may be better to pass down a pathSoFar obj.
 *
 * @param {!Path} relativePath The remainder of the path
 * @param {function(T):boolean} predicate The predicate to satisfy to return a
 * node
 * @return {?{path:!Path, value:!T}}
 */
 ImmutableTree.prototype.findRootMostMatchingPathAndValue = function (relativePath, predicate) {
 if (this.value != null && predicate(this.value)) {
 return { path: Path_1.Path.Empty, value: this.value };
 }
 else {
 if (relativePath.isEmpty()) {
 return null;
 }
 else {
 var front = relativePath.getFront();
 var child = this.children.get(front);
 if (child !== null) {
 var childExistingPathAndValue = child.findRootMostMatchingPathAndValue(relativePath.popFront(), predicate);
 if (childExistingPathAndValue != null) {
 var fullPath = new Path_1.Path(front).child(childExistingPathAndValue.path);
 return { path: fullPath, value: childExistingPathAndValue.value };
 }
 else {
 return null;
 }
 }
 else {
 return null;
 }
 }
 }
 };
 /**
 * Find, if it exists, the shortest subpath of the given path that points a defined
 * value in the tree
 * @param {!Path} relativePath
 * @return {?{path: !Path, value: !T}}
 */
 ImmutableTree.prototype.findRootMostValueAndPath = function (relativePath) {
 return this.findRootMostMatchingPathAndValue(relativePath, function () { return true; });
 };
 /**
 * @param {!Path} relativePath
 * @return {!ImmutableTree.} The subtree at the given path
 */
 ImmutableTree.prototype.subtree = function (relativePath) {
 if (relativePath.isEmpty()) {
 return this;
 }
 else {
 var front = relativePath.getFront();
 var childTree = this.children.get(front);
 if (childTree !== null) {
 return childTree.subtree(relativePath.popFront());
 }
 else {
 return ImmutableTree.Empty;
 }
 }
 };
 /**
 * Sets a value at the specified path.
 *
 * @param {!Path} relativePath Path to set value at.
 * @param {?T} toSet Value to set.
 * @return {!ImmutableTree.} Resulting tree.
 */
 ImmutableTree.prototype.set = function (relativePath, toSet) {
 if (relativePath.isEmpty()) {
 return new ImmutableTree(toSet, this.children);
 }
 else {
 var front = relativePath.getFront();
 var child = this.children.get(front) || ImmutableTree.Empty;
 var newChild = child.set(relativePath.popFront(), toSet);
 var newChildren = this.children.insert(front, newChild);
 return new ImmutableTree(this.value, newChildren);
 }
 };
 /**
 * Removes the value at the specified path.
 *
 * @param {!Path} relativePath Path to value to remove.
 * @return {!ImmutableTree.} Resulting tree.
 */
 ImmutableTree.prototype.remove = function (relativePath) {
 if (relativePath.isEmpty()) {
 if (this.children.isEmpty()) {
 return ImmutableTree.Empty;
 }
 else {
 return new ImmutableTree(null, this.children);
 }
 }
 else {
 var front = relativePath.getFront();
 var child = this.children.get(front);
 if (child) {
 var newChild = child.remove(relativePath.popFront());
 var newChildren = void 0;
 if (newChild.isEmpty()) {
 newChildren = this.children.remove(front);
 }
 else {
 newChildren = this.children.insert(front, newChild);
 }
 if (this.value === null && newChildren.isEmpty()) {
 return ImmutableTree.Empty;
 }
 else {
 return new ImmutableTree(this.value, newChildren);
 }
 }
 else {
 return this;
 }
 }
 };
 /**
 * Gets a value from the tree.
 *
 * @param {!Path} relativePath Path to get value for.
 * @return {?T} Value at path, or null.
 */
 ImmutableTree.prototype.get = function (relativePath) {
 if (relativePath.isEmpty()) {
 return this.value;
 }
 else {
 var front = relativePath.getFront();
 var child = this.children.get(front);
 if (child) {
 return child.get(relativePath.popFront());
 }
 else {
 return null;
 }
 }
 };
 /**
 * Replace the subtree at the specified path with the given new tree.
 *
 * @param {!Path} relativePath Path to replace subtree for.
 * @param {!ImmutableTree} newTree New tree.
 * @return {!ImmutableTree} Resulting tree.
 */
 ImmutableTree.prototype.setTree = function (relativePath, newTree) {
 if (relativePath.isEmpty()) {
 return newTree;
 }
 else {
 var front = relativePath.getFront();
 var child = this.children.get(front) || ImmutableTree.Empty;
 var newChild = child.setTree(relativePath.popFront(), newTree);
 var newChildren = void 0;
 if (newChild.isEmpty()) {
 newChildren = this.children.remove(front);
 }
 else {
 newChildren = this.children.insert(front, newChild);
 }
 return new ImmutableTree(this.value, newChildren);
 }
 };
 /**
 * Performs a depth first fold on this tree. Transforms a tree into a single
 * value, given a function that operates on the path to a node, an optional
 * current value, and a map of child names to folded subtrees
 * @template V
 * @param {function(Path, ?T, Object.):V} fn
 * @return {V}
 */
 ImmutableTree.prototype.fold = function (fn) {
 return this.fold_(Path_1.Path.Empty, fn);
 };
 /**
 * Recursive helper for public-facing fold() method
 * @template V
 * @param {!Path} pathSoFar
 * @param {function(Path, ?T, Object.):V} fn
 * @return {V}
 * @private
 */
 ImmutableTree.prototype.fold_ = function (pathSoFar, fn) {
 var accum = {};
 this.children.inorderTraversal(function (childKey, childTree) {
 accum[childKey] = childTree.fold_(pathSoFar.child(childKey), fn);
 });
 return fn(pathSoFar, this.value, accum);
 };
 /**
 * Find the first matching value on the given path. Return the result of applying f to it.
 * @template V
 * @param {!Path} path
 * @param {!function(!Path, !T):?V} f
 * @return {?V}
 */
 ImmutableTree.prototype.findOnPath = function (path, f) {
 return this.findOnPath_(path, Path_1.Path.Empty, f);
 };
 ImmutableTree.prototype.findOnPath_ = function (pathToFollow, pathSoFar, f) {
 var result = this.value ? f(pathSoFar, this.value) : false;
 if (result) {
 return result;
 }
 else {
 if (pathToFollow.isEmpty()) {
 return null;
 }
 else {
 var front = pathToFollow.getFront();
 var nextChild = this.children.get(front);
 if (nextChild) {
 return nextChild.findOnPath_(pathToFollow.popFront(), pathSoFar.child(front), f);
 }
 else {
 return null;
 }
 }
 }
 };
 /**
 *
 * @param {!Path} path
 * @param {!function(!Path, !T)} f
 * @returns {!ImmutableTree.}
 */
 ImmutableTree.prototype.foreachOnPath = function (path, f) {
 return this.foreachOnPath_(path, Path_1.Path.Empty, f);
 };
 ImmutableTree.prototype.foreachOnPath_ = function (pathToFollow, currentRelativePath, f) {
 if (pathToFollow.isEmpty()) {
 return this;
 }
 else {
 if (this.value) {
 f(currentRelativePath, this.value);
 }
 var front = pathToFollow.getFront();
 var nextChild = this.children.get(front);
 if (nextChild) {
 return nextChild.foreachOnPath_(pathToFollow.popFront(), currentRelativePath.child(front), f);
 }
 else {
 return ImmutableTree.Empty;
 }
 }
 };
 /**
 * Calls the given function for each node in the tree that has a value.
 *
 * @param {function(!Path, !T)} f A function to be called with
 * the path from the root of the tree to a node, and the value at that node.
 * Called in depth-first order.
 */
 ImmutableTree.prototype.foreach = function (f) {
 this.foreach_(Path_1.Path.Empty, f);
 };
 ImmutableTree.prototype.foreach_ = function (currentRelativePath, f) {
 this.children.inorderTraversal(function (childName, childTree) {
 childTree.foreach_(currentRelativePath.child(childName), f);
 });
 if (this.value) {
 f(currentRelativePath, this.value);
 }
 };
 /**
 *
 * @param {function(string, !T)} f
 */
 ImmutableTree.prototype.foreachChild = function (f) {
 this.children.inorderTraversal(function (childName, childTree) {
 if (childTree.value) {
 f(childName, childTree.value);
 }
 });
 };
 ImmutableTree.Empty = new ImmutableTree(null);
 return ImmutableTree;
}());
exports.ImmutableTree = ImmutableTree;

//# sourceMappingURL=ImmutableTree.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/util/ImmutableTree.js
// module id = 23
// module chunks = 0